/**
 * OfferCheckerPage — pre-launch diagnostic for a campaign's tracking link
 * and postback handshake.
 *
 * Operator pastes a tracking URL + postback template (with macro tokens),
 * optionally picks a campaign, hits "Run Check". Backend fetches the URL
 * (manual redirect), inspects the postback for {click_id} + unknown macros,
 * and returns a diagnostic report. UI renders:
 *   - 4 KPI tiles: status code, redirect hops, macros found, postback ready
 *   - issue list (red/amber badges)
 *   - macro substitution preview (local, derived from template)
 *   - raw API response in a collapsible <details>
 *
 * Form layer: react-hook-form + zod resolver (zod 4). Toasts: sonner.
 * Design: emerald-500 primary, sky-500 gradient headline, zinc borders,
 * 6-8px rounding, Inter font inherited from the app root.
 */

import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import {
	ISSUE_LABELS,
	macroPreviewRows,
	runCheckSchema,
	substituteMacros,
	type OfferCheckIssue,
	type OfferCheckRunView,
	type OfferCheckStatus,
	type RunCheckFormValues,
} from "./api";
import { useCampaignsList, useOfferCheckMutation } from "./hooks";

// ---------------------------------------------------------------------------
// Style tokens (DESIGN.md — emerald primary / sky gradient / zinc borders)
// ---------------------------------------------------------------------------

const CARD = "rounded-lg border border-zinc-200 bg-white shadow-sm p-6";
const LABEL = "block text-sm font-medium text-zinc-700 mb-1";
const HELPER = "text-xs text-zinc-500 mt-1";
const ERROR_TEXT = "text-xs text-red-600 mt-1";
const INPUT =
	"w-full border border-zinc-200 rounded-md px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400";
const INPUT_MONO =
	"w-full border border-zinc-200 rounded-md px-3 py-2 text-sm font-mono focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 focus:outline-none placeholder:text-zinc-400";
const BTN_PRIMARY =
	"bg-emerald-500 hover:bg-emerald-600 text-white rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2";
const BTN_SECONDARY =
	"bg-white border border-zinc-200 text-zinc-700 hover:bg-zinc-50 rounded-md px-4 py-2 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed";

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function OfferCheckerPage() {
	const mutation = useOfferCheckMutation();
	const campaigns = useCampaignsList();

	const form = useForm<RunCheckFormValues>({
		defaultValues: {
			campaignId: "",
			url: "",
			postbackTemplate:
				"https://your-tracker.example.com/postback?click_id={click_id}&payout={payout}",
		},
		mode: "onSubmit",
	});

	const watchedTemplate = form.watch("postbackTemplate");
	const previewRows = useMemo(
		() => macroPreviewRows(watchedTemplate ?? ""),
		[watchedTemplate],
	);
	const substituted = useMemo(
		() => substituteMacros(watchedTemplate ?? ""),
		[watchedTemplate],
	);

	function onSubmit(values: RunCheckFormValues) {
		const parsed = runCheckSchema.safeParse(values);
		if (!parsed.success) {
			for (const issue of parsed.error.issues) {
				const path = issue.path[0];
				if (path === "campaignId" || path === "url" || path === "postbackTemplate") {
					form.setError(path, { message: issue.message });
				}
			}
			toast.error("Please fix the highlighted fields.");
			return;
		}
		mutation.mutate(parsed.data, {
			onSuccess: (run) => {
				const label =
					run.status === "pass"
						? "Check passed."
						: run.status === "warn"
							? "Check passed with warnings."
							: "Check failed.";
				if (run.status === "pass") toast.success(label);
				else toast.error(label);
			},
			onError: (err) => {
				toast.error(err.message || "Unable to run check.");
			},
		});
	}

	return (
		<main
			data-testid="offer-checker-page"
			className="flex-1 overflow-auto"
		>
			<div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-6">
				<PageHeader />

				<section className={CARD} aria-labelledby="form-heading">
					<div className="flex flex-col gap-1 mb-6">
						<h2 id="form-heading" className="text-base font-medium text-zinc-900">
							Diagnostic input
						</h2>
						<p className="text-xs text-zinc-500">
							Targets POST /api/v1/offer-checker/run. URL is fetched with redirects
							disabled and the postback template is scanned for the {`{click_id}`}
							macro plus any unknown tokens.
						</p>
					</div>

					<form
						onSubmit={form.handleSubmit(onSubmit)}
						className="flex flex-col gap-5"
						data-testid="offer-checker-form"
						aria-label="offer-checker form"
						noValidate
					>
						<CampaignField
							register={form.register("campaignId", { required: "Campaign ID is required." })}
							error={form.formState.errors.campaignId?.message}
							options={campaigns.data ?? []}
							loading={campaigns.isLoading}
						/>
						<UrlField
							register={form.register("url", { required: "Tracking URL is required." })}
							error={form.formState.errors.url?.message}
						/>
						<PostbackField
							register={form.register("postbackTemplate", {
								required: "Postback template is required.",
							})}
							error={form.formState.errors.postbackTemplate?.message}
						/>

						<div className="flex flex-col gap-2 pt-2 border-t border-zinc-200 sm:flex-row sm:items-center sm:justify-between">
							<p className="text-xs text-zinc-500">
								Macros: {previewRows.length} token
								{previewRows.length === 1 ? "" : "s"} detected.
							</p>
							<div className="flex items-center gap-3">
								<button
									type="button"
									className={BTN_SECONDARY}
									onClick={() => {
										form.reset();
										mutation.reset();
									}}
									disabled={mutation.isPending}
									data-testid="offer-checker-reset"
								>
									Reset
								</button>
								<button
									type="submit"
									className={BTN_PRIMARY}
									disabled={mutation.isPending}
									data-testid="offer-checker-submit"
								>
									{mutation.isPending ? (
										<>
											<Spinner />
											Running check.
										</>
									) : (
										"Run Check"
									)}
								</button>
							</div>
						</div>
					</form>
				</section>

				<MacroPreviewCard
					rows={previewRows}
					substituted={substituted}
					template={watchedTemplate ?? ""}
				/>

				{mutation.isError && (
					<section
						className={`${CARD} border-red-200`}
						role="alert"
						data-testid="offer-checker-error"
					>
						<p className="text-sm text-red-700 font-medium">Error</p>
						<p className="text-sm text-zinc-700 mt-1">
							{mutation.error?.message ?? "Unable to run check."}
						</p>
					</section>
				)}

				{mutation.data && (
					<ResultPanel run={mutation.data} />
				)}
			</div>
		</main>
	);
}

export default OfferCheckerPage;

// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------

function PageHeader() {
	return (
		<header className="flex flex-col gap-2">
			<nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-zinc-500">
				<span className="text-zinc-500">Tools</span>
				<span aria-hidden="true" className="text-zinc-300">
					/
				</span>
				<span className="text-zinc-900 font-medium">Offer Checker</span>
			</nav>
			<h1 className="text-2xl font-semibold bg-gradient-to-r from-emerald-500 to-sky-500 bg-clip-text text-transparent">
				Offer Checker
			</h1>
			<p className="text-sm text-zinc-500 mt-1">
				Validate tracking URLs before launch — redirect chain, macro substitution,
				postback handshake.
			</p>
		</header>
	);
}

// ---------------------------------------------------------------------------
// Form fields
// ---------------------------------------------------------------------------

type RegisterReturn = ReturnType<ReturnType<typeof useForm<RunCheckFormValues>>["register"]>;

function CampaignField(props: {
	register: RegisterReturn;
	error?: string;
	options: Array<{ id: string; name: string }>;
	loading: boolean;
}) {
	const hasOptions = props.options.length > 0;
	return (
		<div>
			<label htmlFor="offer-checker-campaign" className={LABEL}>
				Campaign
			</label>
			{hasOptions ? (
				<select
					id="offer-checker-campaign"
					className={INPUT}
					aria-invalid={Boolean(props.error)}
					{...props.register}
					data-testid="offer-checker-campaign-select"
				>
					<option value="">Select a campaign.</option>
					{props.options.map((opt) => (
						<option key={opt.id} value={opt.id}>
							{opt.name || opt.id}
						</option>
					))}
				</select>
			) : (
				<input
					id="offer-checker-campaign"
					type="text"
					placeholder={props.loading ? "Loading campaigns." : "camp_1234"}
					className={INPUT}
					aria-invalid={Boolean(props.error)}
					{...props.register}
					data-testid="offer-checker-campaign-input"
				/>
			)}
			{props.error ? (
				<p className={ERROR_TEXT} data-testid="offer-checker-campaign-error">
					{props.error}
				</p>
			) : (
				<p className={HELPER}>
					Identifier of the campaign being launched. Auto-populated if /campaigns
					is reachable.
				</p>
			)}
		</div>
	);
}

function UrlField(props: { register: RegisterReturn; error?: string }) {
	return (
		<div>
			<label htmlFor="offer-checker-url" className={LABEL}>
				Tracking URL
			</label>
			<input
				id="offer-checker-url"
				type="url"
				inputMode="url"
				autoComplete="off"
				placeholder="https://track.example.com/click?offer=123"
				className={INPUT_MONO}
				aria-invalid={Boolean(props.error)}
				{...props.register}
				data-testid="offer-checker-url-input"
			/>
			{props.error ? (
				<p className={ERROR_TEXT} data-testid="offer-checker-url-error">
					{props.error}
				</p>
			) : (
				<p className={HELPER}>
					Fetched with redirects disabled; a 2xx or 3xx response is required to
					pass.
				</p>
			)}
		</div>
	);
}

function PostbackField(props: { register: RegisterReturn; error?: string }) {
	return (
		<div>
			<label htmlFor="offer-checker-postback" className={LABEL}>
				Postback template
			</label>
			<textarea
				id="offer-checker-postback"
				rows={3}
				className={INPUT_MONO}
				aria-invalid={Boolean(props.error)}
				{...props.register}
				data-testid="offer-checker-postback-input"
			/>
			{props.error ? (
				<p className={ERROR_TEXT} data-testid="offer-checker-postback-error">
					{props.error}
				</p>
			) : (
				<p className={HELPER}>
					Must contain {`{click_id}`}. Supported macros appear highlighted below.
				</p>
			)}
		</div>
	);
}

function Spinner() {
	return (
		<span
			aria-hidden="true"
			className="inline-block h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/40 border-t-white"
		/>
	);
}

// ---------------------------------------------------------------------------
// Macro preview card (client-side, no network)
// ---------------------------------------------------------------------------

function MacroPreviewCard(props: {
	rows: Array<{ name: string; known: boolean; sample: string }>;
	substituted: string;
	template: string;
}) {
	if (props.rows.length === 0 && props.template.length === 0) return null;
	return (
		<section className={CARD} aria-labelledby="macro-preview-heading">
			<div className="flex flex-col gap-1 mb-4">
				<h2
					id="macro-preview-heading"
					className="text-base font-medium text-zinc-900"
				>
					Macro substitution preview
				</h2>
				<p className="text-xs text-zinc-500">
					Preview shown client-side with sample values — the server performs the
					authoritative check.
				</p>
			</div>
			{props.rows.length > 0 ? (
				<div className="bg-white border border-zinc-200 rounded-lg overflow-hidden">
					<table className="w-full border-collapse text-sm">
						<thead className="bg-zinc-50 text-xs uppercase tracking-wider text-zinc-500">
							<tr>
								<th className="px-4 py-3 text-left font-medium">Macro</th>
								<th className="px-4 py-3 text-left font-medium">Source</th>
								<th className="px-4 py-3 text-left font-medium">Sample value</th>
							</tr>
						</thead>
						<tbody>
							{props.rows.map((row) => (
								<tr
									key={row.name}
									className="hover:bg-emerald-50/40 transition-colors"
									data-testid={`offer-checker-macro-row-${row.name}`}
								>
									<td className="px-4 py-3 border-t border-zinc-200 font-mono text-zinc-900">
										{`{${row.name}}`}
									</td>
									<td className="px-4 py-3 border-t border-zinc-200">
										{row.known ? (
											<Badge tone="active">known</Badge>
										) : (
											<Badge tone="error">unknown</Badge>
										)}
									</td>
									<td className="px-4 py-3 border-t border-zinc-200 font-mono text-zinc-700">
										{row.sample}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			) : (
				<p className="text-xs text-zinc-500">
					No macros in the template yet — add {`{click_id}`} at minimum.
				</p>
			)}
			<div className="mt-4">
				<p className="text-xs text-zinc-500 uppercase tracking-wider font-medium mb-2">
					Rendered postback
				</p>
				<pre
					className="bg-zinc-50 border border-zinc-200 rounded-md p-3 text-xs font-mono text-zinc-800 whitespace-pre-wrap break-all"
					data-testid="offer-checker-rendered-postback"
				>
					{props.substituted || "—"}
				</pre>
			</div>
		</section>
	);
}

// ---------------------------------------------------------------------------
// Result panel — 4 KPI tiles + issues + raw response
// ---------------------------------------------------------------------------

function ResultPanel(props: { run: OfferCheckRunView }) {
	const run = props.run;
	return (
		<section
			className="flex flex-col gap-6"
			aria-labelledby="result-heading"
			data-testid="offer-checker-result"
		>
			<div className="flex items-center justify-between">
				<h2
					id="result-heading"
					className="text-xl font-semibold text-zinc-900"
				>
					Result
				</h2>
				<StatusBadge status={run.status} />
			</div>

			<KpiGrid run={run} />

			<IssueList issues={run.issues} />

			<RedirectChain run={run} />

			<RawResponse run={run} />
		</section>
	);
}

function KpiGrid(props: { run: OfferCheckRunView }) {
	const run = props.run;
	const tiles: Array<{
		label: string;
		value: string;
		tone: "emerald" | "amber" | "red" | "zinc";
		helper?: string;
	}> = [
		{
			label: "Status code",
			value: run.url_status === null ? "—" : String(run.url_status),
			tone: run.url_ok ? "emerald" : run.url_status === null ? "zinc" : "red",
			helper: run.url_error ?? (run.url_ok ? "2xx/3xx" : "Non 2xx/3xx"),
		},
		{
			label: "Redirect hops",
			value: run.url_ok ? "0" : "—",
			tone: "zinc",
			helper:
				run.url_status !== null && run.url_status >= 300 && run.url_status < 400
					? "Redirect detected"
					: "Manual redirect",
		},
		{
			label: "Macros found",
			value: String(run.macros_found.length),
			tone: run.macros_invalid.length > 0 ? "amber" : "emerald",
			helper:
				run.macros_invalid.length > 0
					? `${run.macros_invalid.length} unknown`
					: "All recognised",
		},
		{
			label: "Postback ready",
			value: run.postback_has_click_id ? "Yes" : "No",
			tone: run.postback_has_click_id ? "emerald" : "red",
			helper: run.postback_has_click_id
				? "Contains {click_id}"
				: "Missing {click_id}",
		},
	];
	return (
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
			{tiles.map((tile, idx) => (
				<KpiTile key={idx} {...tile} />
			))}
		</div>
	);
}

function KpiTile(props: {
	label: string;
	value: string;
	tone: "emerald" | "amber" | "red" | "zinc";
	helper?: string;
}) {
	const accent =
		props.tone === "red"
			? "border-l-red-500"
			: props.tone === "amber"
				? "border-l-amber-500"
				: props.tone === "zinc"
					? "border-l-zinc-300"
					: "border-l-emerald-500";
	const helperColor =
		props.tone === "red"
			? "text-red-600"
			: props.tone === "amber"
				? "text-amber-600"
				: "text-zinc-500";
	return (
		<div
			className={`bg-white border border-zinc-200 border-l-4 ${accent} rounded-lg p-5`}
			data-testid={`offer-checker-kpi-${props.label.toLowerCase().replace(/\s+/g, "-")}`}
		>
			<p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
				{props.label}
			</p>
			<p className="text-3xl font-semibold text-zinc-900 mt-2">{props.value}</p>
			{props.helper ? (
				<p className={`text-sm ${helperColor} mt-1`}>{props.helper}</p>
			) : null}
		</div>
	);
}

function IssueList(props: { issues: OfferCheckIssue[] }) {
	if (props.issues.length === 0) {
		return (
			<div
				className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700"
				data-testid="offer-checker-no-issues"
			>
				No issues detected — ready for launch.
			</div>
		);
	}
	return (
		<ul
			className="flex flex-col gap-2"
			data-testid="offer-checker-issue-list"
		>
			{props.issues.map((issue) => (
				<li
					key={issue}
					className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
					data-issue={issue}
				>
					<span
						aria-hidden="true"
						className="mt-1 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-red-500"
					/>
					<span>{ISSUE_LABELS[issue] ?? issue}</span>
				</li>
			))}
		</ul>
	);
}

function RedirectChain(props: { run: OfferCheckRunView }) {
	const run = props.run;
	// The backend doesn't currently expose intermediate redirect hops; we
	// surface the single probe hop to give operators a clear audit trail.
	const hops: Array<{ index: number; url: string; status: number | string }> = [
		{
			index: 1,
			url: run.url,
			status: run.url_status ?? (run.url_error ? `error: ${run.url_error}` : "—"),
		},
	];
	return (
		<section className={CARD} aria-labelledby="redirect-heading">
			<div className="flex flex-col gap-1 mb-4">
				<h2 id="redirect-heading" className="text-base font-medium text-zinc-900">
					Redirect chain
				</h2>
				<p className="text-xs text-zinc-500">
					Single-hop probe (manual redirect). Chains of length &gt; 1 are
					surfaced in a future release.
				</p>
			</div>
			<ol
				className="flex flex-col gap-2"
				data-testid="offer-checker-redirect-chain"
			>
				{hops.map((hop) => (
					<li
						key={hop.index}
						className="flex items-center gap-3 rounded-md border border-zinc-200 bg-white px-3 py-2 text-sm"
					>
						<span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-zinc-100 text-xs font-medium text-zinc-600">
							{hop.index}
						</span>
						<span className="font-mono text-xs text-zinc-700 break-all flex-1">
							{hop.url}
						</span>
						<span className="text-xs font-medium text-zinc-500">
							{hop.status}
						</span>
					</li>
				))}
			</ol>
		</section>
	);
}

function RawResponse(props: { run: OfferCheckRunView }) {
	return (
		<details className="rounded-lg border border-zinc-200 bg-white">
			<summary className="cursor-pointer px-4 py-3 text-sm font-medium text-zinc-700 hover:bg-zinc-50">
				Raw response
			</summary>
			<pre
				className="border-t border-zinc-200 bg-zinc-50 px-4 py-3 text-xs font-mono text-zinc-800 whitespace-pre-wrap break-all overflow-x-auto"
				data-testid="offer-checker-raw-response"
			>
				{JSON.stringify(props.run, null, 2)}
			</pre>
		</details>
	);
}

// ---------------------------------------------------------------------------
// Shared badge
// ---------------------------------------------------------------------------

function Badge(props: {
	tone: "active" | "pending" | "error" | "draft" | "info";
	children: React.ReactNode;
}) {
	const tone =
		props.tone === "active"
			? "bg-emerald-50 text-emerald-700"
			: props.tone === "pending"
				? "bg-amber-50 text-amber-700"
				: props.tone === "error"
					? "bg-red-50 text-red-700"
					: props.tone === "info"
						? "bg-sky-50 text-sky-700"
						: "bg-zinc-100 text-zinc-600";
	return (
		<span
			className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${tone}`}
		>
			{props.children}
		</span>
	);
}

function StatusBadge(props: { status: OfferCheckStatus }) {
	const tone: "active" | "pending" | "error" =
		props.status === "pass"
			? "active"
			: props.status === "warn"
				? "pending"
				: "error";
	const label =
		props.status === "pass"
			? "Pass"
			: props.status === "warn"
				? "Warn"
				: "Fail";
	return (
		<span data-testid={`offer-checker-status-${props.status}`}>
			<Badge tone={tone}>{label}</Badge>
		</span>
	);
}
